import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  LineController,
  BarController,
  DoughnutController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import ChartTopbar, { ChartTopbarProps } from './ChartTopbar';


// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  LineController,
  BarController,
  DoughnutController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController
);

// (Removed unused font loading helper)

export type ChartType = 'doughnut' | 'bar' | 'line' | 'pie' | 'polarArea' | 'radar' | 'scatter';

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    fill?: boolean;
    tension?: number;
    pointBackgroundColor?: string | string[];
    pointBorderColor?: string | string[];
    pointRadius?: number;
    pointHoverRadius?: number;
    minBarLength?: number;
  }[];
}

export interface ChartOptions {
  responsive?: boolean;
  maintainAspectRatio?: boolean;
  aspectRatio?: number | undefined;
  barPercentage?: number;
  categoryPercentage?: number;
  plugins?: {
    legend?: {
      display?: boolean;
      position?: 'top' | 'bottom' | 'left' | 'right';
      usePointStyle?: boolean;
      labels?: {
        usePointStyle?: boolean;
        padding?: number;
        boxWidth?: number;
        boxHeight?: number;
        font?: {
          size?: number;
          family?: string;
          weight?: string;
        };
      };
    };
    title?: {
      display?: boolean;
      text?: string;
      font?: {
        size?: number;
        family?: string;
        weight?: string;
      };
    };
    tooltip?: {
      enabled?: boolean;
      backgroundColor?: string;
      titleColor?: string;
      bodyColor?: string;
      borderColor?: string;
      borderWidth?: number;
    };
  };
  scales?: {
    x?: {
      display?: boolean;
      title?: {
        display?: boolean;
        text?: string;
      };
      grid?: {
        display?: boolean;
        color?: string;
        lineWidth?: number;
        drawBorder?: boolean;
      };
      ticks?: {
        font?: {
          size?: number;
          family?: string;
          weight?: string;
        };
        color?: string;
        stepSize?: number;
        maxRotation?: number;
        minRotation?: number;
        padding?: number;
        callback?: (value: number | string, index: number) => string | string[];
      };
    };
    y?: {
      display?: boolean;
      title?: {
        display?: boolean;
        text?: string;
      };
      grid?: {
        display?: boolean;
        color?: string;
        lineWidth?: number;
        drawBorder?: boolean;
      };
      ticks?: {
        font?: {
          size?: number;
          family?: string;
          weight?: string;
        };
        color?: string;
        stepSize?: number;
        callback?: (value: number | string) => string;
      };
      beginAtZero?: boolean;
      max?: number;
    };
  };
  elements?: {
    arc?: {
      borderWidth?: number;
    };
    bar?: {
      borderWidth?: number;
    };
    line?: {
      borderWidth?: number;
      tension?: number;
    };
    point?: {
      radius?: number;
      hoverRadius?: number;
      borderWidth?: number;
    };
  };
}

interface DashboardChartProps {
  type: ChartType;
  data: ChartData;
  options?: ChartOptions;
  width?: number;
  height?: number;
  chartHeight?: number; // Separate height for the actual chart area
  className?: string;
  loading?: boolean;
  error?: string;
  topbar?: ChartTopbarProps;
  showLegend?: boolean;
  legendNote?: string;
  belowTopbar?: React.ReactNode;
  belowTopbarHeight?: number;
  extraTopMargin?: boolean;
  belowChart?: React.ReactNode;
  customPadding?: string;
  noDataMessage?: string; // Custom message for NO_DATA error
  noDataSubMessage?: string; // Custom sub-message for NO_DATA error
}

const DashboardChart: React.FC<DashboardChartProps> = ({ 
  type,
  data,
  options,
  height = 500,
  chartHeight,
  className = '',
  loading = false,
  error,
  topbar,
  showLegend = true,
  legendNote,
  belowTopbar,
  belowTopbarHeight = 80,
  extraTopMargin = false,
  belowChart,
  customPadding,
  noDataMessage = "No Data Available For The Selected User Role",
  noDataSubMessage = "Please apply the appropriate filters to display the chart data"
}) => {
  const chartOptions = useMemo(() => {
    const defaultOptions: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: undefined,
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          usePointStyle: true, // Default to circles for other charts
          labels: {
            usePointStyle: true, // Default to circles for other charts
            boxWidth: 8,
            boxHeight: 8,
            padding: 20,
            font: {
              size: 12,
              family: 'Montserrat, sans-serif',
              weight: '600'
            },
          }
        },
        title: {
          display: false,
          font: {
            size: 16,
            family: 'Montserrat, sans-serif',
            weight: '600'
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1
        }
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.02)',
            lineWidth: 1,
            drawBorder: false
          },
          ticks: {
            font: {
              size: 12,
              family: 'Montserrat, sans-serif',
              weight: 'bold'
            },
            color: '#666',
            maxRotation: 0,
            minRotation: 0
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.02)',
            lineWidth: 1,
            drawBorder: false
          },
          ticks: {
            font: {
              size: 12,
              family: 'Montserrat, sans-serif',
              weight: '600'
            },
            color: '#666',
            callback: function(value: number | string) {
              return `AFA ${value}`;
            }
          }
        }
      },
      elements: {
        arc: {
          borderWidth: 2
        },
        bar: {
          borderWidth: 1
        },
        line: {
          borderWidth: 2,
          tension: 0.1
        },
        point: {
          radius: 4,
          hoverRadius: 6,
          borderWidth: 2
        }
      }
    };

    // Merge with custom options
    return {
      ...defaultOptions,
      ...options,
      plugins: {
        ...defaultOptions.plugins,
        ...options?.plugins
      },
      scales: {
        ...defaultOptions.scales,
        ...options?.scales
      },
      elements: {
        ...defaultOptions.elements,
        ...options?.elements
      }
    };
  }, [options, showLegend]);

  const chartData = useMemo(() => {
    return {
      labels: data.labels,
      datasets: data.datasets.map(dataset => ({
        ...dataset,
        // Ensure default colors if not provided
        backgroundColor: dataset.backgroundColor || '#36A2EB',
        borderColor: dataset.borderColor || '#36A2EB',
        borderWidth: dataset.borderWidth || 1
      }))
    };
  }, [data]);

  if (loading) {
    return (
      <div 
        className={`relative w-full bg-white rounded-lg border border-outline ${className}`}
        style={{ height: `${height}px`, minHeight: `${height}px`, fontWeight: '500' }}
      >
        {topbar && (
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center">
              {topbar.title && (
                <h3 className="font-semibold text-primary_text" style={{ fontSize: '16px' }}>{topbar.title}</h3>
              )}
              {topbar.leftContent && (
                <div className="ml-4">
                  {topbar.leftContent}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2 opacity-0 pointer-events-none">
              {topbar.rightContent}
            </div>
          </div>
        )}
        <div 
          className="flex items-center justify-center"
          style={{ height: topbar ? `${height - 80}px` : `${height}px` }}
        >
          <div className="flex flex-col items-center space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-600 font-medium">Loading chart...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // Check if it's a "no data" error (not a real error)
    if (error === 'NO_DATA') {
      return (
        <div 
          className={`relative w-full bg-white rounded-lg border border-outline ${className}`}
          style={{ height: `${height}px`, minHeight: `${height}px`, fontWeight: '500' }}
        >
          {topbar && <ChartTopbar {...topbar} />}
          <div className="flex items-center justify-center" style={{ height: topbar ? `${height - 80}px` : `${height}px` }}>
            <div className="text-center px-4">
              <p className="text-primary_text text-lg font-semibold mb-2">
                {noDataMessage}
              </p>
              <p className="text-secondary_text text-sm">
                {noDataSubMessage}
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show regular error for other error types
    return (
      <div 
        className={`relative w-full bg-white rounded-lg border border-outline ${className}`}
        style={{ height: `${height}px`, minHeight: `${height}px`, fontWeight: '500' }}
      >
        {topbar && <ChartTopbar {...topbar} />}
        <div className="flex items-center justify-center bg-red-50 rounded-lg" style={{ height: topbar ? `${height - 80}px` : `${height}px` }}>
          <div className="text-center">
            <div className="text-red-600 text-lg font-semibold mb-2">Error</div>
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative w-full bg-white rounded-lg border border-outline ${className}`}
      style={{ height: `${height}px`, minHeight: `${height}px`, fontWeight: '500' }}
    >
      {topbar && <ChartTopbar {...topbar} />}
      {belowTopbar && (
        <div className="px-4">
          {belowTopbar}
        </div>
      )}
      <div 
        className={`${customPadding ? customPadding : 'p-10'} ${extraTopMargin ? '-mt-[7.5rem] pt-[7.5rem]' : ''}`}
        style={{ height: chartHeight ? `${chartHeight}px` : (topbar || belowTopbar ? `${height - ((topbar ? 80 : 0) + (belowTopbar ? belowTopbarHeight : 0))}px` : `${height}px`)}}
      >
        <Chart
          type={type}
          data={chartData}
          options={chartOptions as unknown as Record<string, unknown>}
        />
      </div>
      {belowChart && (
        <div className="px-4">
          {belowChart}
        </div>
      )}
      {showLegend && legendNote && (
        <div className="px-10 pb-4 text-center w-full">
          <p className="-mt-10 text-secondary_text font-normal text-sm">
            {legendNote}
          </p>
        </div>
      )}
    </div>
  );
};

export default DashboardChart;
